// =============================================================================
// Module Project - Store: useSidebarProjectsStore
// Gestion des projets et compteurs pour la sidebar (Zustand global)
// =============================================================================

import { create } from 'zustand';
import {
  fetchAllProjects,
  reorderProjectsInSpace,
  moveProjectToSpace,
} from '../data/projectRepository';
import { fetchOpenTaskCountsByProject } from '../data/taskRepository';
import type { Project } from '../types';
import { showActionError } from '@/lib/permission-errors';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface SidebarProjectsState {
  projects: Project[];
  taskCounts: Record<string, number>;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  loadProjects: (orgId: string) => Promise<Project[]>;
  loadTaskCounts: (orgId: string, projectIds: string[]) => Promise<void>;
  loadSidebarData: (orgId: string) => Promise<void>;
  /** Increment or decrement the open task count for a project */
  adjustTaskCount: (projectId: string, delta: number) => void;
  /**
   * Optimistically reorder/move a project across spaces, then persist.
   * If targetSpaceId === undefined, only reordering inside its current space is performed.
   */
  reorderOrMoveProject: (
    projectId: string,
    targetSpaceId: string | null | undefined,
    newIndex: number,
    orgId: string,
  ) => Promise<void>;
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────
export const useSidebarProjectsStore = create<SidebarProjectsState>()((set, get) => ({
  projects: [],
  taskCounts: {},
  loading: false,
  initialized: false,
  error: null,

  // ───────────────────────────────────────────────────────────────────────────
  // LOAD PROJECTS (all projects including archived)
  // ───────────────────────────────────────────────────────────────────────────
  loadProjects: async (orgId: string) => {
    set({ loading: true, error: null });

    try {
      const projects = await fetchAllProjects(orgId);
      set({ projects, taskCounts: {}, loading: false, initialized: true });
      return projects;
    } catch (err) {
      console.error('Error loading sidebar projects:', err);
      set({
        loading: false,
        error: 'Erreur lors du chargement des projets',
      });
      return get().projects;
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // LOAD TASK COUNTS
  // ───────────────────────────────────────────────────────────────────────────
  loadTaskCounts: async (orgId: string, projectIds: string[]) => {
    if (projectIds.length === 0) {
      set({ taskCounts: {} });
      return;
    }

    try {
      const counts = await fetchOpenTaskCountsByProject(orgId, projectIds);
      set({ taskCounts: counts });
    } catch (err) {
      console.error('Error loading task counts:', err);
      // Don't set error state for task counts - non-critical
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // LOAD ALL (convenience method)
  // ───────────────────────────────────────────────────────────────────────────
  loadSidebarData: async (orgId: string) => {
    const projects = await get().loadProjects(orgId);
    if (projects.length > 0) {
      await get().loadTaskCounts(orgId, projects.map(p => p.id));
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // ADJUST TASK COUNT (increment/decrement without DB round-trip)
  // ───────────────────────────────────────────────────────────────────────────
  adjustTaskCount: (projectId: string, delta: number) => {
    set(state => ({
      taskCounts: {
        ...state.taskCounts,
        [projectId]: Math.max(0, (state.taskCounts[projectId] || 0) + delta),
      }
    }));
  },

  // ───────────────────────────────────────────────────────────────────────────
  // DRAG & DROP: reorder within a space, or move across spaces
  // ───────────────────────────────────────────────────────────────────────────
  reorderOrMoveProject: async (projectId, targetSpaceId, newIndex, orgId) => {
    const previousProjects = get().projects;
    const moved = previousProjects.find(p => p.id === projectId);
    if (!moved) return;

    const isCrossSpace = targetSpaceId !== undefined && targetSpaceId !== moved.space_id;
    const effectiveTargetSpaceId = (targetSpaceId === undefined ? moved.space_id : targetSpaceId) ?? null;

    // Build an optimistic ordering for the affected bucket
    const sameBucket = (p: Project) =>
      (p.space_id ?? null) === effectiveTargetSpaceId && p.id !== projectId;

    const bucket = previousProjects.filter(sameBucket);
    const others = previousProjects.filter(p => !sameBucket(p) && p.id !== projectId);

    const clampedIndex = Math.max(0, Math.min(newIndex, bucket.length));
    const updatedMoved: Project = { ...moved, space_id: effectiveTargetSpaceId, position: clampedIndex };
    const newBucket = [...bucket.slice(0, clampedIndex), updatedMoved, ...bucket.slice(clampedIndex)]
      .map((p, idx) => ({ ...p, position: idx }));

    set({ projects: [...others, ...newBucket] });

    try {
      if (isCrossSpace) {
        await moveProjectToSpace(projectId, effectiveTargetSpaceId, clampedIndex);
      } else {
        await reorderProjectsInSpace(newBucket.map(p => p.id));
      }
      // Refresh from server to stay in sync (positions, ordering, RLS)
      await get().loadProjects(orgId);
    } catch (err) {
      console.error('reorderOrMoveProject failed:', err);
      set({ projects: previousProjects });
      showActionError(err, isCrossSpace ? 'project.move' : 'project.reorder');
      throw err;
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // RESET
  // ───────────────────────────────────────────────────────────────────────────
  reset: () => {
    set({
      projects: [],
      taskCounts: {},
      loading: false,
      initialized: false,
      error: null,
    });
  },
}));
